// components/EditDocumentModal.jsx
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { AuthContext } from "../contexts/auth";
import RecipientsField from "./recipients_field";
import FilePreviewModal from "./preview_modal";
import { updateDocumentV2, getDocumentRecipientsDefault, updateSignedDocumentDeadline } from "../services/api";
import { normalizeEmailCsv, sameEmailSet, buildDefaultEmailCsv } from "../services/utils_email";


/* ---------------- helpers ---------------- */

const toTimingNumberOrNull = (s) => {
  const raw = String(s ?? "").trim();
  if (raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
};


const normalizeTimingStr = (s) => {
  const raw = String(s ?? "").trim();
  if (raw === "") return "";          // default-mode
  if (!/^\d+$/.test(raw)) return raw;
  return String(parseInt(raw, 10));   // normalizza "01" -> "1"
};

const hasNotificationsChanged = ({
  original,
  timingInput,
  emailInput,
  defaultTimingStr,
  defaultEmailCsv,
}) => {
  // cosa vedeva/aveva "effettivamente" prima (custom oppure default)
  const beforeTiming =
    original.notification_timing == null ? normalizeTimingStr(defaultTimingStr) : String(original.notification_timing);
  const beforeEmail =
    original.notification_email == null ? normalizeEmailCsv(defaultEmailCsv) : normalizeEmailCsv(original.notification_email);

  // cosa vuole ora (se input vuoto => default visuale)
  const afterTiming =
    normalizeTimingStr(timingInput) === "" ? normalizeTimingStr(defaultTimingStr) : normalizeTimingStr(timingInput);
  const afterEmail =
    String(emailInput || "").trim() === "" ? normalizeEmailCsv(defaultEmailCsv) : normalizeEmailCsv(emailInput);

  return String(beforeTiming) !== String(afterTiming) || beforeEmail !== afterEmail;
};


const clampTiming = (n) => {
  if (n == null) return null;
  let x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return null;
  if (x < 1) x = 1;
  if (x > 31) x = 31;
  return x;
};

const buildEffectiveNotifications = ({
  timingInput,
  emailInput,
  defaultTimingStr,
  defaultEmailCsv,
}) => {
  // se vuoto => usa default; se default mancante => null
  const effectiveTiming = clampTiming(
    String(timingInput || "").trim() === "" ? defaultTimingStr : timingInput
  );

  const effectiveEmailCsv = normalizeEmailCsv(
    String(emailInput || "").trim() === "" ? defaultEmailCsv : emailInput
  );

  // default normalizzati
  const defaultTiming = clampTiming(defaultTimingStr);
  const defaultEmail = normalizeEmailCsv(defaultEmailCsv);

  const matchesDefault =
    effectiveTiming === defaultTiming && sameEmailSet(effectiveEmailCsv, defaultEmail);

  return { effectiveTiming, effectiveEmailCsv, matchesDefault, defaultTiming, defaultEmail };
};

const isValidDueDateRange = (value) => {
  if (!value) return true;

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split("-").map(Number);

  if (year < 2000 || year > 2200) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);

  const isRealDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  return isRealDate;
};

/* ---------------- component ---------------- */

export default function EditDocumentModal({
  open,
  onClose,
  onReopen,
  doc,
  onSaved,
  mode = "view",
  documentType = "document",
  onRequestEdit,
  allowInlineEditToggle = true,
  showPreviewModal = true,
  noButtonPreview = '',
}) {
  const { token, profile } = useContext(AuthContext);
  const isSignedDocument = documentType === "signed_document";
  const documentEmployees = isSignedDocument ? (Array.isArray(doc?.employees) ? doc.employees : []) : [];
  const hasMultipleEmployees = isSignedDocument && documentEmployees.length > 1;

  // ---------- mode ----------
  const [isEdit, setIsEdit] = useState(mode === "edit");
  useEffect(() => setIsEdit(mode === "edit"), [mode, open, doc?.id]);

  // ---------- base fields ----------
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(""); // YYYY-MM-DD

  // INPUT utente (possono essere "" per “default”)
  const [timingInput, setTimingInput] = useState(""); // "" o "N"
  const [emailInput, setEmailInput] = useState(""); // "" o csv

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [previewDoc, setPreviewDoc] = useState(null);
  const [reopenAfterPreview, setReopenAfterPreview] = useState(false);

  // ---------- suggested defaults ----------
  const [suggested, setSuggested] = useState({
    loading: false,
    loaded: false,
    defaultDays: null,
    sendCompany: false,
    sendHandlers: false,
    companyEmail: "",
    companyLabel: "Azienda",
    handlers: [],
    othersEmails: "",
  });

  const cacheRef = useRef(new Map()); // employeeId -> payload
  const employeeId = isSignedDocument ? (documentEmployees[0]?.id ?? null) : (doc?.employee?.id ?? doc?.employee_id ?? null);

  const levelValues = Object.values(profile?.level || {}).flat();
  const hasShiftsLevel = levelValues.includes("shifts");

  // ---------- original snapshot (DB) ----------
  const original = useMemo(() => {
    if (!doc) return null;

    const rawTiming = doc.notification_timing; // number|null
    const rawEmail = doc.notification_email; // string|null

    return {
      id: doc.id,
      name: doc.name || "",
      description: doc.description ?? "",
      due_date: doc.due_date ? dayjs(doc.due_date).format("YYYY-MM-DD") : "",
      // valori DB
      notification_timing: rawTiming === undefined ? null : rawTiming,
      notification_email: rawEmail == null ? null : String(rawEmail).trim(),
    };
  }, [doc]);

  // ---------- fetch defaults ----------
  useEffect(() => {
    if (!open || !token || !employeeId) return;

    const fromCache = cacheRef.current.get(employeeId);
    if (fromCache) {
      setSuggested({ loading: false, loaded: true, ...fromCache });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setSuggested((p) => ({ ...p, loading: true }));

        const data = await getDocumentRecipientsDefault(token, employeeId);
        if (cancelled) return;
                
        const payload = {
          defaultDays: data?.default_days ?? null,
          sendCompany: !!data?.send_company,
          sendHandlers: !!data?.send_handlers,
          companyEmail: String(data?.company_email || "").trim().toLowerCase(),
          companyLabel: String(data?.company_label || "Azienda").trim(),
          handlers: Array.isArray(data?.handlers_emails) ? data.handlers_emails : [],
          othersEmails: String(data?.others_emails || ""),
        };
        
        cacheRef.current.set(employeeId, payload);
        setSuggested({ loading: false, loaded: true, ...payload });
      } catch {
        if (cancelled) return;
        setSuggested({
          loading: false,
          loaded: true,
          defaultDays: null,
          sendCompany: false,
          sendHandlers: false,
          companyEmail: "",
          companyLabel: "Azienda",
          handlers: [],
          othersEmails: "",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, token, employeeId]);

  // ---------- derived defaults (visuali) ----------
  const defaultTimingStr = useMemo(() => {
    return suggested.loaded && suggested.defaultDays != null ? String(suggested.defaultDays) : "";
  }, [suggested.loaded, suggested.defaultDays]);

  const defaultEmailCsv = useMemo(() => {
    if (!suggested.loaded) return "";
    if (suggested?.defaultDays == null) return "";
    return buildDefaultEmailCsv({
      send_company: suggested.sendCompany,
      send_handlers: suggested.sendHandlers,
      company_email: suggested.companyEmail,
      handlers_emails: suggested.handlers,
      others_emails: suggested.othersEmails,
    });
  }, [suggested.loaded, suggested.sendCompany, suggested.sendHandlers, suggested.companyEmail, suggested.handlers, suggested.othersEmails]);

  // ---------- init form on open/doc change ----------
  useEffect(() => {
    if (!open || !original) return;

    setName(original.name);
    setDescription(original.description || "");
    setDueDate(original.due_date || "");
    setErrors({});

    // se DB è null -> mostro default ma input resta "" (modalità default)
    setTimingInput(original.notification_timing == null ? "" : String(original.notification_timing));
    // Se doc usa i default (null) -> precarica defaultEmailCsv
    // NB: aspetta suggested.loaded per avere defaultEmailCsv corretto
    if (original.notification_email == null) {
      setEmailInput(defaultEmailCsv || "");
    } else {
      setEmailInput(String(original.notification_email));
    }
  }, [open, original?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- what the user sees (if input blank => show defaults) ----------
  const shownTiming = String(timingInput || defaultTimingStr || "").trim();
  const shownEmail = String(emailInput || defaultEmailCsv || "").trim();

  const hasDueDate = !!dueDate;

  const isReminderEnabled = useMemo(() => {
    if (!hasDueDate) return false;

    const n = toTimingNumberOrNull(shownTiming);
    return n != null && n >= 1;
  }, [hasDueDate, shownTiming]);

  // ---------- validation ----------
  const validate = useCallback(() => {
    if (!isEdit) return {};
    const e = {};

    if (dueDate && !isValidDueDateRange(dueDate)) {
      e.due_date = [
        "La data di scadenza deve essere compresa tra il 01/01/2000 e il 31/12/2200."
      ];
    }

    const { effectiveTiming, effectiveEmailCsv } = buildEffectiveNotifications({
      timingInput,
      emailInput,
      defaultTimingStr,
      defaultEmailCsv,
    });

    const timingEnabled = effectiveTiming != null && effectiveTiming >= 1;

    if (hasDueDate && timingEnabled && !effectiveEmailCsv) {
      e.notification_email = ["Devi inserire almeno un indirizzo email per inviare il promemoria."];
    }

    if (!String(name || "").trim()) {
      e.name = ["Il nome documento è obbligatorio."];
    }

    // valida input timing SOLO se l’utente ha scritto qualcosa
    if (String(timingInput || "").trim() !== "") {
      const n = clampTiming(timingInput);
      if (n == null) {
        e.notification_timing = ["Il promemoria deve essere un numero tra 1 e 31, oppure vuoto per usare il default."];
      }
    }

    return e;
  }, [isEdit, timingInput, emailInput, defaultTimingStr, defaultEmailCsv, dueDate, name]);

  // ---------- cancel edit ----------
  const handleCancelEdit = () => {
    if (saving || !original) return;
    setIsEdit(false);

    setName(original.name);
    setDescription(original.description || "");
    setDueDate(original.due_date || "");
    setTimingInput(original.notification_timing == null ? "" : String(original.notification_timing));
    setEmailInput(original.notification_email == null ? "" : String(original.notification_email));
    setErrors({});
  };

  const switchToEdit = () => {
    if (onRequestEdit) onRequestEdit();
    else if (allowInlineEditToggle) setIsEdit(true);
  };

  // ---------- save (notifiche: se cambia qualcosa => tutto custom) ----------
  const handleSave = async () => {
    if (!doc || !isEdit || !original) return;

    setErrors({});

    // DOCUMENTI CON FIRMA
    if (isSignedDocument) {

      // Stesse validazioni dei documenti ordinari
      const e = validate();

      if (Object.keys(e).length) {
        setErrors(e);
        return;
      }

      // Conferma eliminazione della scadenza
      if (original.due_date && !dueDate) {
        const result = await Swal.fire({
          icon: "warning",
          title: "Eliminare la scadenza?",
          text: "Rimuovendo la data di scadenza verranno disattivati anche il promemoria e le email di notifica.",
          showCancelButton: true,
          confirmButtonText: "Sì, elimina",
          cancelButtonText: "Annulla",
          confirmButtonColor: "#4f46e5",
          cancelButtonColor: "#6b7280",
        });

        if (!result.isConfirmed) return;
      }

      const patch = {};

      // DATA SCADENZA
      if (dueDate !== (original.due_date || "")) {
        patch.due_date = dueDate
          ? `${dueDate}T00:00:00`
          : null;
      }

      // NOTIFICHE
      if (!dueDate) {
        patch.notification_timing = null;
        patch.notification_email = null;
      } else {
        const {
          effectiveTiming,
          effectiveEmailCsv,
          matchesDefault,
        } = buildEffectiveNotifications({
          timingInput,
          emailInput,
          defaultTimingStr,
          defaultEmailCsv,
        });

        if (matchesDefault) {
          patch.notification_timing = null;
          patch.notification_email = null;
        } else {
          patch.notification_timing = effectiveTiming;
          patch.notification_email = effectiveEmailCsv || null;
        }
      }

      // Evita aggiornamenti inutili
      if (
        patch.notification_email != null &&
        original.notification_email != null &&
        sameEmailSet(
          patch.notification_email,
          original.notification_email
        )
      ) {
        delete patch.notification_email;
      }

      if (
        patch.notification_email === null &&
        original.notification_email === null
      ) {
        delete patch.notification_email;
      }

      if (
        patch.notification_timing === original.notification_timing
      ) {
        delete patch.notification_timing;
      }

      if (Object.keys(patch).length === 0) {
        onClose?.();
        return;
      }

      setSaving(true);

      try {
        const updated = await updateSignedDocumentDeadline(token, doc.id, patch, "payrolls");
        const merged = {...doc, ...patch, ...updated, id: doc.id};

        onSaved?.(merged);

        Swal.fire({
          icon: "success",
          title: "Documento aggiornato",
          timer: 1500,
          showConfirmButton: false,
        });

        onClose?.();

      } catch (err) {
        if (
          err?.response?.data &&
          typeof err.response.data === "object"
        ) {
          setErrors(err.response.data);
        } else {
          setErrors({generico: [err?.message || "Errore durante l'aggiornamento del documento."],});
        }
      } finally {
        setSaving(false);
      }

      return;
    }
    
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    const hadDueDate = Boolean(original.due_date);
    const removedDueDate = hadDueDate && !dueDate;

    if (removedDueDate) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Eliminare la scadenza?",
        text: "Rimuovendo la data di scadenza verranno disattivati anche il promemoria e le email di notifica.",
        showCancelButton: true,
        confirmButtonText: "Sì, elimina",
        cancelButtonText: "Annulla",
        confirmButtonColor: "#4f46e5",
        cancelButtonColor: "#6b7280",
      });
      if (!result.isConfirmed) return;
    }

    const patch = {};

    // name/description/due_date
    if (name !== original.name) patch.name = name;

    if ((description ?? "") !== (original.description ?? "")) {
      patch.description = String(description || "").trim() === "" ? null : description;
    }

    if (dueDate !== (original.due_date || "")) {
      patch.due_date = dueDate ? `${dueDate}T00:00:00` : null;
    }

    // -------- NOTIFICHE: sempre confronto con document-recipients-default --------
    if (!dueDate) {
      patch.notification_timing = null;
      patch.notification_email = null;
    } else {
      const {
        effectiveTiming,
        effectiveEmailCsv,
        matchesDefault,
      } = buildEffectiveNotifications({
        timingInput,
        emailInput,
        defaultTimingStr,
        defaultEmailCsv,
      });

      if (matchesDefault) {
        // se il risultato equivale al default => null/null
        patch.notification_timing = null;
        patch.notification_email = null;
      } else {
        // se differisce anche solo uno => tutto custom
        patch.notification_timing = effectiveTiming;           // number
        patch.notification_email = effectiveEmailCsv || null;  // csv
      }
    }

    // evita patch inutile (se combacia con DB già così)
    if (
      patch.notification_email != null &&
      original.notification_email != null &&
      sameEmailSet(patch.notification_email, original.notification_email)
    ) {
      delete patch.notification_email;
    }
    if (patch.notification_email === null && original.notification_email === null) {
      delete patch.notification_email;
    }

    if (Object.keys(patch).length === 0) {
      onClose && onClose();
      return;
    }

    setSaving(true);
    try {
      const updated = await updateDocumentV2(token, doc.id, patch);
      const merged = { ...doc, ...updated, id: doc.id };
      onSaved && onSaved(merged);

      Swal.fire({ icon: "success", title: "Documento aggiornato", timer: 1500, showConfirmButton: false });
      onClose && onClose();
    } catch (err) {
      if (err?.response?.data && typeof err.response.data === "object") setErrors(err.response.data);
      else setErrors({ generico: [err?.message || "Errore imprevisto."] });
    } finally {
      setSaving(false);
    }
  };

  const renderFieldError = (field) =>
    errors?.[field] ? <p className="text-red-600 text-xs mt-1">{errors[field].join(", ")}</p> : null;

  const title = isSignedDocument
    ? (isEdit ? "Modifica scadenza documento con firma" : "Dettagli documento con firma")
    : (isEdit ? "Modifica documento" : "Dettagli documento");

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" slotProps={{ root: { sx: { zIndex: 999 } } }}>
        <DialogTitle component="div" className="bg-indigo-200">
          <div className="flex items-center justify-between gap-3">
            <div className="font-semibold text-indigo-800">{title}</div>

            {!noButtonPreview && (
              <>
                {showPreviewModal && !isEdit && (
                  <span>
                    <button
                      type="button"
                      onClick={() => {
                        if (!doc?.file) return;
                        setPreviewDoc({ url: doc.file, name: doc.name, description: doc.description });
                        setReopenAfterPreview(true);
                        onClose && onClose();
                      }}
                      disabled={!doc?.file}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold
                        ${doc?.file ? "bg-indigo-700 text-white hover:bg-indigo-500" : "bg-white/40 text-indigo-300 cursor-not-allowed"}`}
                      title={!doc?.file ? "File non disponibile" : "Apri anteprima"}
                    >
                      <MagnifyingGlassIcon className="h-4 w-4" />
                      Apri anteprima
                    </button>
                  </span>
                )} 
              </>
            )}
          </div>
        </DialogTitle>

        <DialogContent dividers>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 mb-0">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {hasMultipleEmployees ? "Dipendenti" : "Dipendente"}
              </label>

              {isSignedDocument ? (
                documentEmployees.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {documentEmployees.map(employee => (
                      <p key={employee.id} className="text-sm font-bold text-indigo-800">
                        {[employee.name].filter(Boolean).join(" ") || employee.cf || "—"}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-800">
                    {doc?.employees_names || "—"}
                  </p>
                )
              ) : (
                <p className="text-sm text-gray-800">
                  {doc?.employee?.first_name || doc?.employee?.last_name
                    ? `${doc.employee.first_name ?? ""} ${doc.employee.last_name ?? ""}`.trim()
                    : doc?.employee_name || doc?.employee?.cf || "—"}
                </p>
              )}
            </div>

            <div className="mb-0">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nome documento</label>
              {isSignedDocument ? (
                <div className="bg-gray-100 shadow-sm border border-gray-200 rounded-md py-2 px-3 text-gray-700 min-h-10">
                  {name || "—"}
                </div>
              ) : (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    if (!isEdit) return;

                    setName(e.target.value);

                    if (errors.name) {
                      setErrors((p) => ({ ...p, name: undefined }));
                    }
                  }}
                  readOnly={!isEdit}
                  disabled={!isEdit}
                  className={`shadow border rounded w-full py-2 px-3 focus:outline-none
                    ${isEdit
                      ? "hover:border-indigo-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                      : "bg-gray-100 shadow-sm border border-gray-200 rounded-md cursor-default text-gray-700"
                    }`}
                />
              )}
              {renderFieldError("name")}
            </div>

            <div className="mb-0">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Descrizione</label>
              <input
                type="text"
                value={description ?? ""}
                onChange={(e) => {if (isEdit && !isSignedDocument) {setDescription(e.target.value);}}}
                readOnly={!isEdit || isSignedDocument}
                disabled={!isEdit || isSignedDocument}
                className={`shadow border rounded w-full py-2 px-3 focus:outline-none
                  ${(isEdit && !isSignedDocument)
                    ? "hover:border-indigo-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                    : "bg-gray-100 shadow-sm border border-gray-200 rounded-md cursor-default text-gray-700"
                  }`}
              />
            </div>

            <div className="mb-0">
                <div className="flex justify-between">
                <label className="block text-sm font-semibold text-gray-700 sm:mb-5">
                  Data scadenza
                </label>
                {isEdit && dueDate && (
                  <button
                    type="button"
                    onClick={() => {
                      setDueDate("");
                      setErrors((p) => ({ ...p, due_date: undefined }));
                    }}
                    className="flex text-xs text-red-600 underline hover:text-red-800 "
                  >
                    Rimuovi data
                  </button>
                )}
              </div>
              <input
                type="date"
                value={dueDate}
                min="2000-01-01"
                max="2200-12-31"
                onChange={(e) => {
                  if (!isEdit) return;
                  setDueDate(e.target.value);
                  if (errors.due_date) setErrors((p) => ({ ...p, due_date: undefined }));
                }}
                inputMode="numeric"
                disabled={!isEdit}
                className={`shadow border rounded w-full py-2 px-3 focus:outline-none
                  ${isEdit ? "hover:border-indigo-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200" : "bg-gray-100 shadow-sm border border-gray-200 rounded-md cursor-default text-gray-700"}`}
              />
              {renderFieldError("due_date")}
            </div>
            
            {dueDate && (
              <>

                <div className="mb-0">
                  <label className="block text-sm font-semibold text-gray-700 mb-0">Promemoria scadenza</label>
                  <div className="text-gray-500 font-normal text-xs mb-1">
                    {suggested?.defaultDays !== null ? 'Giorni da 1 a 31, campo vuoto per l\'impostazione predefinita' : 'Giorni da 1 a 31'}
                    {defaultTimingStr && (
                      <span className="pl-2">({defaultTimingStr} giorni)</span>
                    )}
                  </div>

                  {isEdit ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder={suggested.loading ? "Caricamento default…" : (defaultTimingStr ? `${defaultTimingStr} giorni` : "Inserire giorni")}
                      value={timingInput}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v !== "" && !/^\d+$/.test(v)) return; // solo cifre o vuoto
                        setTimingInput(v);
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.notification_timing;
                          delete next.notification_email;
                          delete next.due_date;
                          return next;
                        });
                      }}
                      onBlur={() => {
                        // clamp SOLO se c'è valore
                        const raw = String(timingInput || "").trim();
                        if (raw === "") return;
                        let n = parseInt(raw, 10);
                        if (Number.isNaN(n)) n = 7;
                        if (n < 1) n = 1;
                        if (n > 31) n = 31;
                        setTimingInput(String(n));
                      }}
                      className="w-full h-10 px-3 rounded-md shadow-sm border border-gray-500
                        hover:border-indigo-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200
                        text-gray-700 text-md bg-white"
                    />
                  ) : (
                    <div className="h-10 flex items-center px-3 rounded-md bg-gray-100 shadow-sm border border-gray-200 text-gray-700 text-md">
                      {original?.notification_timing == null
                        ? (defaultTimingStr ? `${defaultTimingStr} giorni (predefinito)` : "Disabilitato")
                        : `${original.notification_timing} giorni`}
                    </div>
                  )}

                  {renderFieldError("notification_timing")}
                </div>

                {isReminderEnabled && (
                  <div className="sm:col-span-2 mb-0">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email di notifica scadenza</label>

                    <div className="border border-gray-200 rounded-md">
                      <RecipientsField
                        token={token}
                        value={shownEmail}
                        onChange={(commaSeparated) => {
                          if (!isEdit) return;
                          setEmailInput(commaSeparated);
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.notification_email;
                            return next;
                          });
                        }}
                        disabled={false}
                        isEdit={isEdit}
                        openDirection="top"
                        companyEmail={suggested.companyEmail}
                        companyName={suggested.companyLabel}
                        handlers={suggested.handlers}
                        othersEmails={suggested.othersEmails} 
                        className="bg-gray-100 rounded-md py-2"
                        hasShiftsLevel={hasShiftsLevel}
                      />
                    </div>

                    {renderFieldError("notification_email")}
                  </div>
                )}
              </>
            )}

            {errors?.generico && (
              <div className="sm:col-span-2">
                <p className="text-red-600 text-xs mt-2">{errors.generico.join(", ")}</p>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", gap: 3, p: 2 }}>
          {isEdit ? (
            <>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="uppercase bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-gray-300 w-28"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="uppercase font-semibold text-white text-sm px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 w-28"
              >
                {saving ? "Salvataggio…" : "Salva"}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="uppercase bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-md hover:bg-gray-300 w-28"
              >
                Chiudi
              </button>
              <button
                onClick={switchToEdit}
                className="uppercase font-semibold text-white text-sm px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 w-28"
              >
                Modifica
              </button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {previewDoc && (
        <FilePreviewModal
          doc={previewDoc}
          onClose={() => {
            setPreviewDoc(null);
            if (reopenAfterPreview) {
              setReopenAfterPreview(false);
              onReopen && onReopen();
            }
          }}
        />
      )}
    </>
  );
}